import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch.jsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select.jsx';
import {
  Loader2,
  Save,
  Eye,
  EyeOff,
  CheckCircle2,
  XCircle,
  Key,
  Server,
  Gauge,
} from 'lucide-react';
import { toast } from 'sonner';

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [revealed, setRevealed] = useState({});
  const [testResults, setTestResults] = useState({});
  const [form, setForm] = useState({});

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await adminAPI.getSettings();
      setSettings(res.data);
      setForm(res.data);
    } catch {
      toast.error('Impossible de charger les paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Ne soumettre que les champs modifiés
      const updates = {};
      for (const [key, value] of Object.entries(form)) {
        if (value !== settings[key]) {
          updates[key] = value;
        }
      }
      // Ne pas soumettre les clés masquées non modifiées
      const maskedFields = ['openai_api_key', 'anthropic_api_key', 'github_token', 'github_client_secret'];
      for (const field of maskedFields) {
        if (updates[field] && updates[field].includes('●')) {
          delete updates[field];
        }
      }
      if (Object.keys(updates).length === 0) {
        toast.info('Aucune modification');
        setSaving(false);
        return;
      }
      await adminAPI.updateSettings(updates);
      toast.success('Paramètres sauvegardés');
      loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleTestKey = async (keyName) => {
    setTestResults((prev) => ({ ...prev, [keyName]: 'testing' }));
    try {
      const res = await adminAPI.testKey(keyName);
      setTestResults((prev) => ({ ...prev, [keyName]: res.data.status }));
      if (res.data.status === 'ok') {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch {
      setTestResults((prev) => ({ ...prev, [keyName]: 'error' }));
      toast.error('Erreur lors du test');
    }
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleReveal = (field) => {
    setRevealed((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const renderApiKeyField = (field, label) => {
    const value = form[field] || '';
    const isRevealed = revealed[field];
    const testStatus = testResults[field];

    return (
      <div className="space-y-2">
        <Label className="text-xs font-medium flex items-center gap-2">
          {label}
          {testStatus === 'ok' && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
          {testStatus === 'error' && <XCircle className="w-3.5 h-3.5 text-red-400" />}
          {testStatus === 'testing' && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
        </Label>
        <div className="flex gap-2">
          <Input
            type={isRevealed ? 'text' : 'password'}
            value={value}
            onChange={(e) => updateField(field, e.target.value)}
            placeholder="Entrez la clé..."
            className="flex-1 font-mono text-xs"
          />
          <Button variant="ghost" size="sm" onClick={() => toggleReveal(field)}>
            {isRevealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleTestKey(field)}
            disabled={testStatus === 'testing'}
          >
            Tester
          </Button>
        </div>
        {value && value.includes('(env)') && (
          <p className="text-[10px] text-muted-foreground">Chargé depuis le fichier .env</p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Paramètres système</h2>
          <p className="text-sm text-muted-foreground">Configuration des clés API et limites</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="glow-primary">
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Sauvegarder
        </Button>
      </div>

      {/* Clés API */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Key className="w-4 h-4 text-primary" />
            Clés API
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {renderApiKeyField('openai_api_key', 'OpenAI API Key')}
          {renderApiKeyField('anthropic_api_key', 'Anthropic API Key')}
          {renderApiKeyField('github_token', 'GitHub Token')}

          <div className="flex items-center justify-between pt-2">
            <div>
              <Label className="text-xs font-medium">Activer Anthropic</Label>
              <p className="text-[10px] text-muted-foreground">
                Utiliser Claude comme provider LLM
              </p>
            </div>
            <Switch
              checked={form.enable_anthropic ?? true}
              onCheckedChange={(val) => updateField('enable_anthropic', val)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Ollama / Inférence locale */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Server className="w-4 h-4 text-primary" />
            Inférence locale (Ollama)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs">URL Ollama</Label>
              <Input
                value={form.ollama_base_url || ''}
                onChange={(e) => updateField('ollama_base_url', e.target.value)}
                placeholder="http://localhost:11434"
                className="text-xs"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Modèle</Label>
              <Input
                value={form.ollama_model || ''}
                onChange={(e) => updateField('ollama_model', e.target.value)}
                placeholder="qwen2.5-coder:7b"
                className="text-xs"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Mode d'inférence</Label>
            <Select
              value={form.mode_inference || 'hybrid'}
              onValueChange={(val) => updateField('mode_inference', val)}
            >
              <SelectTrigger className="text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hybrid">Hybride (local puis payant)</SelectItem>
                <SelectItem value="local">Local uniquement (Ollama)</SelectItem>
                <SelectItem value="paid">Payant uniquement</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Provider payant</Label>
            <Select
              value={form.paid_provider || 'openai'}
              onValueChange={(val) => updateField('paid_provider', val)}
            >
              <SelectTrigger className="text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="openai">OpenAI</SelectItem>
                <SelectItem value="anthropic">Anthropic</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Limites */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Gauge className="w-4 h-4 text-primary" />
            Limites et budget
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs">Budget quotidien (EUR)</Label>
              <Input
                type="number"
                value={form.default_daily_budget_eur ?? 5}
                onChange={(e) => updateField('default_daily_budget_eur', parseFloat(e.target.value))}
                min={0.1}
                step={0.1}
                className="text-xs"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Étapes max par run</Label>
              <Input
                type="number"
                value={form.max_steps_per_run ?? 20}
                onChange={(e) => updateField('max_steps_per_run', parseInt(e.target.value))}
                min={1}
                max={100}
                className="text-xs"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Requêtes max/minute</Label>
              <Input
                type="number"
                value={form.max_requests_per_minute ?? 20}
                onChange={(e) => updateField('max_requests_per_minute', parseInt(e.target.value))}
                min={1}
                className="text-xs"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Retries max (local)</Label>
              <Input
                type="number"
                value={form.max_local_retries ?? 3}
                onChange={(e) => updateField('max_local_retries', parseInt(e.target.value))}
                min={0}
                max={10}
                className="text-xs"
              />
            </div>
          </div>

          <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
            <div>
              <Label className="text-xs font-medium">Validation du plan requise</Label>
              <p className="text-[10px] text-muted-foreground">
                L'utilisateur doit approuver le plan avant l'exécution
              </p>
            </div>
            <Switch
              checked={form.require_plan_validation ?? false}
              onCheckedChange={(val) => updateField('require_plan_validation', val)}
            />
          </div>

          <div className="flex items-center justify-between mt-3">
            <div>
              <Label className="text-xs font-medium">Mode développement</Label>
              <p className="text-[10px] text-muted-foreground">
                Réponses mock sans clés API
              </p>
            </div>
            <Switch
              checked={form.development_mode ?? false}
              onCheckedChange={(val) => updateField('development_mode', val)}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
